import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import { Loader2 } from 'lucide-react';
import { ConversationState } from '@/enums';
import useTranslation from '@/hooks/useTranslation';
import PaperAirplaneIcon from '../icons/PaperAirplaneIcon';
import { Button } from '../ui/button';
import { CategoryApiResponse, InsightsSubset, Media, SelectedCategory } from '@/types';
import QuestionStimuli from './QuestionStimuli';
// resolveTranslation moved to zone components
import { useGetQuestionCategories } from '@/reactQuery/get';
import { useGetQuestionMedia } from '@/reactQuery/questionMedia';
import StoryZone from './zones/StoryZone';
import CategoriesZone from './zones/CategoriesZone';
import ResponsesZone from './zones/ResponsesZone';
import EvidenceZone from './zones/EvidenceZone';
import DataZone from './zones/DataZone';
import ResponsesPanel from './ResponsesPanel';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';

interface QuestionItemProps {
  isPendingReRun: boolean;
  selectedQuestionInsights?: InsightsSubset;
  questionID: string;
  questionText: string;
  analysisLanguage: string;
  newConversationsCount: number;
  onReRun: (f_id?: string | null, force?: boolean, early?: boolean) => Promise<void> | void;
  isRerunLoading: boolean;
  newConversationsState: ConversationState;
  projectId?: string;
  isPendingInterviewQuestions: boolean;
  questionStimuli?: Media[];
}

function QuestionItem({
  isPendingReRun,
  selectedQuestionInsights,
  questionID,
  questionText,
  analysisLanguage,
  newConversationsCount,
  onReRun,
  isRerunLoading,
  newConversationsState,
  projectId,
  isPendingInterviewQuestions,
  questionStimuli,
}: QuestionItemProps) {
  const { lang } = useTranslation();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const rerunFilter = localStorage.getItem(`rerun_filter_${filterId}`);

  // Data fetching
  const { data: questionMedia = [], isLoading: isMediaLoading } = useGetQuestionMedia(
    projectId,
    questionID,
    filterId,
  );

  // Panel store. `toggleCategory` powers the chart-bar additive
  // multi-select (used by `onSelectCategoryHandler` below) and
  // `resetForQuestion` clears the inline category filter whenever
  // the user navigates to a different question, so stale labels
  // don't leak across questions.
  //
  // Subscribed via individual selectors (not destructuring the
  // whole store) so QuestionItem doesn't re-render when unrelated
  // store slices change — e.g. `selectedCategories` updates from
  // a chip toggle would otherwise force QuestionItem to re-render
  // even though its handlers don't depend on the selection value.
  // Both functions have stable refs from Zustand's create() so
  // these selectors return the same value across renders.
  const toggleCategory = useResponsesPanelStore((s) => s.toggleCategory);
  const resetForQuestion = useResponsesPanelStore((s) => s.resetForQuestion);

  // Derived state
  const noEnoughInterviewData =
    selectedQuestionInsights?.summary?.length === 0 &&
    selectedQuestionInsights?.themes?.length === 0 &&
    selectedQuestionInsights?.quotes?.length === 0 &&
    selectedQuestionInsights?.opportunities?.length === 0 &&
    (!selectedQuestionInsights?.patterns_and_trends ||
      selectedQuestionInsights.patterns_and_trends.length === 0) &&
    (!selectedQuestionInsights?.voice_quotes || selectedQuestionInsights.voice_quotes.length === 0);
  // TELV1-268 — filters exist on LIVE projects and no longer auto-run at
  // create, so "filter selected, no analysis yet" is a routine state on
  // ANY status — it needs a run affordance, not a dead end.
  const isFilteredWithoutAnalysis = noEnoughInterviewData && !!filterId;
  const isReRunInProgress = isPendingReRun || isRerunLoading;
  const dataWithStepsIsLoading = isPendingReRun || isPendingInterviewQuestions;

  // TELV1-296 UX commit 7 — legacy Themes & Opportunities fallback only
  // renders when the new-schema `patterns_and_trends` field is absent AND
  // there is at least one theme or opportunity to show. Lifted out of
  // DataZone so QuestionItem can conditionally render the entire wrapper
  // (otherwise an empty `<div id="qsection-themes">` would inherit the
  // parent's `gap-8` and leave a stray 32px void at the bottom of the
  // page on new-schema projects).
  const hasLegacyThemes =
    !selectedQuestionInsights?.patterns_and_trends?.length &&
    (selectedQuestionInsights?.themes?.length || 0) +
      (selectedQuestionInsights?.opportunities?.length || 0) >
      0;
  const isApplyingFilter = (rerunFilter || isRerunLoading) && Boolean(filterId);
  const isRunning = (!filterId || (filterId && isRerunLoading)) && !rerunFilter;
  let messageKey = '';
  if (isApplyingFilter) {
    messageKey = 'msg.applyingFilter';
  } else if (isRunning) {
    messageKey = 'msg.running';
  }

  // Categories
  const { data: categoriesData, isPending: isPendingCategories } = useGetQuestionCategories(
    filterId,
    projectId,
    questionID,
  );
  const categories = categoriesData as CategoryApiResponse | undefined;

  const showCategoryLoadingIndicator =
    isPendingCategories && (!categories?.categories || categories.categories.length === 0);

  // Rerun filter cleanup. TELV1-268 — no `noEnoughInterviewData` guard:
  // an idle state means nothing is running regardless of whether the
  // scope has data yet, and a stale marker on a never-analyzed filter
  // would otherwise stick forever and hide the run affordance below.
  useEffect(() => {
    if (!newConversationsState || !selectedQuestionInsights || newConversationsState === 'done')
      return;
    if (rerunFilter && newConversationsState === 'idle') {
      localStorage.removeItem(`rerun_filter_${filterId}`);
    }
  }, [rerunFilter, filterId, newConversationsState, selectedQuestionInsights]);

  // Category click in the chart → toggle that category in/out of
  // the inline filter. We deliberately do NOT auto-open the
  // ResponsesPanel — the inline chip filter row + filtered cards
  // above the slide-out give immediate feedback in place. The
  // panel remains available via "Search & sort" for advanced
  // controls. This restores the spirit of the pre-redesign
  // "click chart bar → see filtered answers inline" workflow
  // without forcing the user into a drawer first.
  const onSelectCategoryHandler = useCallback(
    (selectedCategory: SelectedCategory) => {
      toggleCategory(questionID, {
        label: selectedCategory.label,
        value: selectedCategory.label,
      });
    },
    [questionID, toggleCategory],
  );

  // Reset the inline category filter whenever the user navigates
  // to a different question. Without this, chips selected on Q1
  // persist into Q2 even though their labels likely don't exist
  // in Q2's category set — producing an empty inline view that
  // looks broken. This was one of the documented reasons the
  // original redesign rejected inline filtering; we have to
  // handle it explicitly now that the filter lives in a global
  // store rather than in component-local state.
  useEffect(() => {
    if (questionID) resetForQuestion(questionID);
  }, [questionID, resetForQuestion]);

  // Loading/rerun state
  if ((noEnoughInterviewData || rerunFilter) && isRerunLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full min-h-[50vh] text-center">
        <Button variant="default" disabled className="flex items-center gap-2 font-bold px-7">
          <Loader2 className="h-5 w-5 animate-spin" />
          <p>{lang.get(messageKey)}</p>
        </Button>
      </div>
    );
  }

  // No data with active filter — offer the segment's run explicitly
  // (TELV1-268: filters no longer auto-run at create; first runs are
  // never milestone-gated). Mirrors the Overview empty-state card.
  if (isFilteredWithoutAnalysis) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full min-h-[50vh] text-center gap-3">
        {isReRunInProgress ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-neutral-500">Analysis is running for this filter...</p>
          </>
        ) : (
          <div className="contents">
            {newConversationsCount ? (
              <>
                <p className="font-medium text-neutral-700">No AI analysis yet for this segment</p>
                <p className="max-w-md text-sm text-neutral-500">
                  Transcripts, exports and category counts for this segment are already live. Run AI
                  analysis to generate the per-question insights.
                </p>
                <Button
                  variant="default"
                  className="flex items-center gap-2 mt-2 font-bold px-5"
                  onClick={() => onReRun(filterId)}
                >
                  {`Run AI analysis · ${newConversationsCount} matching`}
                </Button>
              </>
            ) : (
              <>
                <p className="font-medium text-neutral-700">
                  {lang.get('msg.noInterviewsThatMatchThisFilter')}
                </p>
                <p className="text-sm text-neutral-500">
                  No completed interviews match this filter yet.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // No data — show run button
  if (noEnoughInterviewData && !rerunFilter) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full min-h-[65vh] text-center">
        <Button
          variant="default"
          disabled={isReRunInProgress}
          className="relative flex items-center gap-2 mt-5 font-bold px-7"
          onClick={() => onReRun()}
        >
          {isReRunInProgress ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <PaperAirplaneIcon className="size-4" />
          )}
          {isReRunInProgress ? 'Running...' : 'Run'}
          {!isRerunLoading && newConversationsCount > 0 && (
            <span className="absolute flex items-center justify-center h-5 px-1.5 text-xs text-white bg-red-600 rounded-full w-fit -top-1 -right-1.5">
              {newConversationsCount}
            </span>
          )}
        </Button>
      </div>
    );
  }

  // === MAIN CONTENT: Zone-based layout ===
  return (
    <div className="flex flex-col w-full gap-8 py-4">
      {/* Analysis in progress banner */}
      {isRerunLoading && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg text-sm text-primary">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span>Analysis is updating — results will refresh automatically.</span>
        </div>
      )}

      {/* Question Stimuli — context for what was asked */}
      {questionStimuli && questionStimuli.length > 0 && <QuestionStimuli media={questionStimuli} />}

      {/* ZONE 1: THE STORY — Summary */}
      <div id="qsection-summary" className="scroll-mt-32">
        <StoryZone
          summary={selectedQuestionInsights?.summary}
          isLoading={dataWithStepsIsLoading}
          analysisLanguage={analysisLanguage}
          voiceQuotes={selectedQuestionInsights?.voice_quotes}
          fallbackQuestionId={questionID}
        />
      </div>

      {/* ZONE 2: CATEGORIES — Thematic-code breakdown.
          TELV1-296 UX commit 6 — QA feedback: "categories sit all the
          way at the bottom, can we move it up, below the summary?".
          Categories answer "what did people say, at a glance" and are
          one of the most-consulted artefacts on the page — placing
          them directly under the Summary surfaces them on the first
          viewport instead of requiring a full-page scroll. The legacy
          Themes & Opportunities block remains in DataZone at the bottom. */}
      <div id="qsection-categories" className="scroll-mt-32">
        <CategoriesZone
          categories={categories}
          isPendingCategories={isPendingCategories}
          analysisLanguage={analysisLanguage}
          onSelectCategory={onSelectCategoryHandler}
          projectId={projectId}
          questionID={questionID}
          questionText={questionText}
          isPendingInterviewQuestions={isPendingInterviewQuestions}
          showCategoryLoadingIndicator={showCategoryLoadingIndicator}
        />
      </div>

      {/* ZONE 3: RESPONDENT ANSWERS — Voice of customer */}
      <div id="qsection-answers" className="scroll-mt-32">
        {questionID && <ResponsesZone questionId={questionID} categoryData={categories} />}
      </div>

      {/* ZONE 4: THE EVIDENCE — Findings + Quotes + Visuals */}
      <div id="qsection-evidence" className="scroll-mt-32">
        <EvidenceZone
          insights={selectedQuestionInsights}
          isLoading={dataWithStepsIsLoading}
          analysisLanguage={analysisLanguage}
          questionMedia={questionMedia}
          isMediaLoading={isMediaLoading}
          voiceQuotes={selectedQuestionInsights?.voice_quotes}
          fallbackQuestionId={questionID}
        />
      </div>

      {/* ZONE 5: DEEPER SYNTHESIS — Themes & Opportunities (legacy schema only).
          Conditionally rendered to avoid a stray 32px (parent gap-8) gap
          below Evidence when the schema is new (patterns_and_trends populated)
          and there are no legacy themes/opportunities — the common case.
          The same check is mirrored in Questions.tsx to hide the SectionNav
          entry when this zone is hidden, so the nav stays in sync with the
          rendered DOM. */}
      {hasLegacyThemes && (
        <div id="qsection-themes" className="scroll-mt-32">
          <DataZone
            insights={selectedQuestionInsights}
            isLoading={dataWithStepsIsLoading}
            analysisLanguage={analysisLanguage}
          />
        </div>
      )}

      {/* Responses slide-out panel. We pass categoryData so the panel
          can offer inline category editing via SingleCategoryCell /
          MultipleCategoryCell on each ResponseCard. */}
      <ResponsesPanel categoryData={categories} />
    </div>
  );
}

export default QuestionItem;
